import { supabase } from '../supabase/client.js';

const CONTRACTOR_TABLE = 'vim_pay_contractor_profiles';
const TAX_DOCUMENT_TABLE = 'vim_pay_tax_documents';
const TAX_DOCUMENT_BUCKET = 'vim-pay-tax-documents';
const MAX_TAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

const PHASE1_SELECT = 'id,auth_user_id,display_name,status,onboarding_status,created_at';
const CONTRACTOR_SELECT = [
  'id','auth_user_id','email','display_name','legal_name','phone','address_line1','address_line2','city','state_region','postal_code','country_code','status','onboarding_status','engagement_type','union_status','union_name','union_local','fringe_profile_key','payout_method','payout_contact','tax_status','commission_rate_percent','profile_completed_at','created_at','updated_at',
].join(',');
const TAX_DOCUMENT_SELECT = 'id,contractor_id,document_type,object_path,status,submission_source,form_revision,document_sha256,certification_accepted,certified_at,certified_by_name,is_current,superseded_at,submitted_at,reviewed_at';

function tableUnavailable(error) {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '');
  return code === '42P01'
    || code === 'PGRST205'
    || /relation .*vim_pay_contractor_profiles.* does not exist/i.test(message)
    || /could not find the table .*vim_pay_contractor_profiles/i.test(message);
}

function phase2ColumnsUnavailable(error) {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '');
  return code === '42703'
    || code === 'PGRST204'
    || /column .* does not exist/i.test(message)
    || /could not find .* column/i.test(message);
}

function phase2ResourceUnavailable(error) {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '');
  return code === '42P01'
    || code === 'PGRST205'
    || /relation .*vim_pay_tax_documents.* does not exist/i.test(message)
    || /could not find the table .*vim_pay_tax_documents/i.test(message);
}

function clean(value) {
  const result = String(value ?? '').trim();
  return result || null;
}

function accessResult(profile, phase2Configured = true) {
  const status = String(profile?.status || '').toLowerCase();
  return {
    configured: true,
    phase2Configured,
    authorized: Boolean(profile && ['invited', 'active'].includes(status)),
    profile: profile || null,
  };
}

async function sha256Hex(file) {
  if (!globalThis.crypto?.subtle) throw new Error('Secure document fingerprinting is not available in this browser.');
  const bytes = await file.arrayBuffer();
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join('');
}

export async function loadMyContractorAccess(userId) {
  if (!supabase || !userId) {
    return { configured: Boolean(supabase), phase2Configured: false, authorized: false, profile: null };
  }

  const expanded = await supabase
    .from(CONTRACTOR_TABLE)
    .select(CONTRACTOR_SELECT)
    .eq('auth_user_id', userId)
    .maybeSingle();

  if (!expanded.error) return accessResult(expanded.data, true);

  if (phase2ColumnsUnavailable(expanded.error)) {
    const fallback = await supabase
      .from(CONTRACTOR_TABLE)
      .select(PHASE1_SELECT)
      .eq('auth_user_id', userId)
      .maybeSingle();
    if (fallback.error) throw fallback.error;
    return accessResult(fallback.data, false);
  }

  if (tableUnavailable(expanded.error)) {
    return { configured: false, phase2Configured: false, authorized: false, profile: null };
  }

  throw expanded.error;
}

export async function saveMyContractorOnboarding(userId, values = {}) {
  if (!supabase || !userId) throw new Error('VIM Pay session is unavailable.');

  const { data: current, error: currentError } = await supabase
    .from(CONTRACTOR_TABLE)
    .select('engagement_type')
    .eq('auth_user_id', userId)
    .maybeSingle();
  if (currentError) throw currentError;

  const engagementType = String(current?.engagement_type || '').toLowerCase();
  const allowedPayouts = engagementType === 'commission_contractor'
    ? new Set(['paypal', 'zelle', 'ach'])
    : new Set(['ach', 'check']);
  const requestedPayout = String(values.payout_method || '').toLowerCase();
  const payoutMethod = allowedPayouts.has(requestedPayout) ? requestedPayout : null;

  // Classification fields are CMS-controlled. This payload intentionally excludes:
  // auth_user_id, status, onboarding_status, engagement_type, union_status,
  // union_name, union_local, fringe_profile_key and tax status.
  const updates = {
    display_name: clean(values.display_name),
    legal_name: clean(values.legal_name),
    phone: clean(values.phone),
    address_line1: clean(values.address_line1),
    address_line2: clean(values.address_line2),
    city: clean(values.city),
    state_region: clean(values.state_region),
    postal_code: clean(values.postal_code),
    country_code: String(values.country_code || 'US').trim().toUpperCase().slice(0, 2) || 'US',
    payout_method: payoutMethod,
    payout_contact: clean(values.payout_contact),
    updated_at: new Date().toISOString(),
  };

  if (['ach', 'check'].includes(updates.payout_method)) updates.payout_contact = null;

  const { data, error } = await supabase
    .from(CONTRACTOR_TABLE)
    .update(updates)
    .eq('auth_user_id', userId)
    .select(CONTRACTOR_SELECT)
    .maybeSingle();

  if (error) throw error;
  return data || null;
}

export async function listMyTaxDocuments(contractorId) {
  if (!supabase || !contractorId) return [];
  const expanded = await supabase
    .from(TAX_DOCUMENT_TABLE)
    .select(TAX_DOCUMENT_SELECT)
    .eq('contractor_id', contractorId)
    .order('submitted_at', { ascending: false });

  if (!expanded.error) return Array.isArray(expanded.data) ? expanded.data : [];
  if (phase2ResourceUnavailable(expanded.error)) return [];

  if (phase2ColumnsUnavailable(expanded.error)) {
    const fallback = await supabase
      .from(TAX_DOCUMENT_TABLE)
      .select('id,contractor_id,document_type,object_path,status,submitted_at,reviewed_at')
      .eq('contractor_id', contractorId)
      .order('submitted_at', { ascending: false });
    if (fallback.error) return [];
    return Array.isArray(fallback.data) ? fallback.data : [];
  }

  throw expanded.error;
}

export async function submitMyW9Document({
  userId,
  contractorId,
  file,
  source = 'manual_upload',
  formRevision = null,
  certificationAccepted = false,
  certifiedByName = null,
}) {
  if (!supabase || !userId || !contractorId) throw new Error('VIM Pay session is unavailable.');
  if (!file) throw new Error('Choose or generate a W-9 document.');
  if (Number(file.size || 0) > MAX_TAX_DOCUMENT_BYTES) throw new Error('W-9 files must be 10 MB or smaller.');
  if (!['manual_upload', 'vim_pay_generated'].includes(source)) throw new Error('Unsupported W-9 submission source.');

  const allowedTypes = source === 'vim_pay_generated'
    ? new Set(['application/pdf'])
    : new Set(['application/pdf', 'image/jpeg', 'image/png']);
  if (file.type && !allowedTypes.has(file.type)) throw new Error(source === 'vim_pay_generated' ? 'Generated W-9 must be a PDF.' : 'W-9 must be a PDF, JPG, or PNG file.');

  if (source === 'vim_pay_generated') {
    if (formRevision !== '2024-03') throw new Error('Generated W-9 must use the approved March 2024 revision.');
    if (!certificationAccepted || !clean(certifiedByName)) throw new Error('Electronic W-9 certification and signature are required.');
  }

  const documentSha256 = await sha256Hex(file);
  const extension = file.type === 'image/jpeg' ? 'jpg' : file.type === 'image/png' ? 'png' : 'pdf';
  const random = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const objectPath = `${userId}/${Date.now()}-${random}-w9.${extension}`;

  const { error: uploadError } = await supabase.storage
    .from(TAX_DOCUMENT_BUCKET)
    .upload(objectPath, file, { cacheControl: '0', upsert: false, contentType: file.type || 'application/pdf' });
  if (uploadError) throw uploadError;

  const { data, error } = await supabase
    .from(TAX_DOCUMENT_TABLE)
    .insert({
      contractor_id: contractorId,
      document_type: 'w9',
      object_path: objectPath,
      submission_source: source,
      form_revision: formRevision,
      document_sha256: documentSha256,
      certification_accepted: Boolean(certificationAccepted),
      certified_by_name: clean(certifiedByName),
    })
    .select(TAX_DOCUMENT_SELECT)
    .single();

  if (error) {
    await supabase.storage.from(TAX_DOCUMENT_BUCKET).remove([objectPath]).catch(() => null);
    throw error;
  }
  return data || null;
}

export async function uploadMyW9({ userId, contractorId, file }) {
  return submitMyW9Document({ userId, contractorId, file, source: 'manual_upload' });
}
